import { z } from 'zod';

import { MusicRegistry } from '../registries/MusicRegistry';
import type { ToolExecutionContext } from '../types';

import { MusicSearchTool } from './MusicSearchTool';
import { VideoStateTool } from './VideoStateTool';

export const setMusicSchema = z.object({
    music_id: z
        .string()
        .optional()
        .describe(
            'ID of the music from the music registry to apply. Required unless clearing music.',
        ),
    clear: z
        .boolean()
        .default(false)
        .describe('Set to true to clear/remove existing music from the video'),
});
export type SetMusicArgs = z.infer<typeof setMusicSchema>;

export class SetMusicTool extends VideoStateTool<typeof setMusicSchema> {
    public static readonly toolName = 'set_music';

    protected override readonly schema = setMusicSchema;
    protected override readonly toolName = SetMusicTool.toolName;
    protected override readonly toolDescription =
        'Apply music to the video or clear existing music';

    public override getToolDisplayName(): string {
        return 'Setting music';
    }

    public get statusIcon(): string {
        return 'music_note';
    }

    public async execute(args: SetMusicArgs, context: ToolExecutionContext) {
        const { music_id, clear = false } = args;

        try {
            if (clear) {
                if (this._luminary.music === null) {
                    return 'No music is currently applied to the video.';
                }
                this._luminary.music = null;
                return 'Successfully cleared music from the video.';
            }

            if (!music_id) {
                return 'Error: music_id is required when not clearing music.';
            }

            const musicRegistry = MusicRegistry.getInstance();
            const audioSourceData = musicRegistry.getAudioSourceData(music_id);

            if (!audioSourceData) {
                return `Error: Music with ID "${music_id}" not found in registry. Please search for music first using the ${MusicSearchTool.toolName} tool.`;
            }

            this._luminary.music = audioSourceData;

            const representation = musicRegistry.getRepresentation(music_id);
            const trackName = representation?.title || 'Unknown track';

            return `Successfully applied music: "${trackName}" to the video.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting music: ${errorMessage}`;
        }
    }
}
